import { createStore, createHook } from 'react-sweet-state';
import type { StoreActionApi } from 'react-sweet-state';

import type {
	TopLevelComment as BaseComment,
	CommentReply as BaseReply,
} from '@confluence/inline-comments-queries';

export interface AnnotationStatus {
	annotationId: string;
	isLoaded: boolean;
}

export interface ReplyData extends BaseReply {
	isUnread: boolean;
}

export interface CommentData extends BaseComment {
	isUnread: boolean;
	isOpen: boolean; // unresolved comment
	replies: ReplyData[];
}

export type CommentsDataState = {
	orderedActiveAnnotationIdList: AnnotationStatus[];
	inlineCommentsDataMap: Record<string, CommentData>;
};

export const initialState: CommentsDataState = {
	orderedActiveAnnotationIdList: [],
	inlineCommentsDataMap: {},
};

export const actions = {
	setOrderedActiveAnnotationIdList:
		(newCommentsList: string[]) =>
		({ setState }: StoreActionApi<CommentsDataState>) => {
			setState({
				orderedActiveAnnotationIdList: newCommentsList.map((annotationId: string) => ({
					annotationId,
					isLoaded: false,
				})),
			});
		},
	updateIsLoadedStatus:
		(loadedAnnotationIds: string[]) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			const { orderedActiveAnnotationIdList } = getState();
			const updatedList = orderedActiveAnnotationIdList.map((annotation) => ({
				...annotation,
				isLoaded: loadedAnnotationIds.includes(annotation.annotationId)
					? true
					: annotation.isLoaded,
			}));
			setState({ orderedActiveAnnotationIdList: updatedList });
		},
	updateInlineCommentsDataMap:
		(annotation: string, comment: CommentData) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			const { inlineCommentsDataMap } = getState();
			setState({
				inlineCommentsDataMap: {
					...inlineCommentsDataMap,
					[annotation]: comment,
				},
			});
		},
	addReplyToCommentThread:
		(parentMarkerRef: string, reply: ReplyData) =>
		({ setState, getState }: StoreActionApi<CommentsDataState>) => {
			const { inlineCommentsDataMap } = getState();
			const parentCommentThread = inlineCommentsDataMap[parentMarkerRef];
			if (parentCommentThread) {
				parentCommentThread.replies.push(reply);
				setState({
					inlineCommentsDataMap: {
						...inlineCommentsDataMap,
						[parentMarkerRef]: parentCommentThread,
					},
				});
			}
		},
};

export const CommentsDataStore = createStore({
	initialState,
	actions,
	name: 'CommentsDataStore',
});

export const useCommentsData = createHook(CommentsDataStore);
